/**
 * useOnline — small hook that mirrors `navigator.onLine` and stays in sync
 * with the browser's `online` / `offline` events.
 *
 * Used by the AppShell offline banner (AC-099) and inside CaptureSheet to
 * disable the parse/save buttons when the device is offline.
 */
import * as React from 'react';

function readOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  // navigator.onLine is true by default in SSR / older browsers.
  return navigator.onLine !== false;
}

export function useOnline(): boolean {
  const [online, setOnline] = React.useState<boolean>(readOnline);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    function onUp() {
      setOnline(true);
    }
    function onDown() {
      setOnline(false);
    }
    window.addEventListener('online', onUp);
    window.addEventListener('offline', onDown);
    // Re-sync once on mount in case the initial paint missed a state change.
    setOnline(readOnline());
    return () => {
      window.removeEventListener('online', onUp);
      window.removeEventListener('offline', onDown);
    };
  }, []);

  return online;
}
